import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { userService } from "../../services/api/user.service";

export const getUsers = createAsyncThunk(
  "user/getUsers",
  async (params, { rejectWithValue }) => {
    try {
      // debugger;
      const response = await userService.getUsers(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);
export const createUser = createAsyncThunk(
  "user/createUser",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await userService.createUser(userData);
      // Check if the response indicates success
      if (response.data && response.data.success === false) {
        return rejectWithValue(
          response.data.message || "Failed to create user"
        );
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create user"
      );
    }
  }
);
export const getUserRoles = createAsyncThunk(
  "user/getUserRoles",

  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.getUserRoles(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user roles"
      );
    }
  }
);
export const getUserById = createAsyncThunk(
  "user/getUserById",

  async (userId, { rejectWithValue }) => {
    try {
      // debugger;
      const response = await userService.getUserById(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch user"
      );
    }
  }
);
export const updateUser = createAsyncThunk(
  "user/updateUser",

  async (userData, { rejectWithValue }) => {
    try {
      const response = await userService.updateUser(userData);
      // Check if the response indicates success
      if (response.data && response.data.success === false) {
        return rejectWithValue(
          response.data.message || "Failed to update user"
        );
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update user"
      );
    }
  }
);
export const deleteUser = createAsyncThunk(
  "user/deleteUser",

  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.deleteUser(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete user"
      );
    }
  }
);
export const searchUsers = createAsyncThunk(
  'user/searchUsers',
  async (searchParams, { rejectWithValue }) => {
    try {
      const response = await userService.getSearchUsers(searchParams);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to search users');
    }
  }
);
export const resetUserPassword = createAsyncThunk(
  'user/resetUserPassword',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.resetPassword(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reset password');
    }
  }
);

export const getUserEffectivePermissions = createAsyncThunk(
  'user/getUserEffectivePermissions',
  async ({ userId, roleId }, { rejectWithValue }) => {
    try {
      const response = await userService.getUserEffectivePermissions(userId, roleId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch user effective permissions');
    }
  }
);

export const activateUser = createAsyncThunk(
  'user/activateUser',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await userService.activateUser(userId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to activate user');
    }
  }
);

export const deactivateUser = createAsyncThunk(
  'user/deactivateUser',
  async ({ userId, reason }, { rejectWithValue }) => {
    try {
      const response = await userService.deactivateUser(userId, reason);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to deactivate user');
    }
  }
);


const initialState = {
  users: [],
  currentUser: null,
  isLoading: false,
  error: null,
  totalCount: 0,
  userRolesResponse: null, // Store full roles API response
  searchResults: [], // Initialize searchResults as an empty array
  userEffectivePermissions: null, // Store user effective permissions
  effectivePermissionsLoading: false,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  }
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },

    clearSearchResults: (state) => {
      state.searchResults = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.data;

        // Store pagination data if available
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
          state.totalCount = action.payload.pagination.total || action.payload.data?.length || 0;
        } else {
          state.totalCount = action.payload.data?.length || 0;
        }
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getUserRoles.fulfilled, (state, action) => {
        state.userRolesResponse = action.payload;
      })
      .addCase(getUserById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentUser = action.payload;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(searchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.searchResults = [];
      })
      .addCase(searchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.searchResults = action.payload.data || [];
      })
      .addCase(searchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.searchResults = [];
      }).addCase(resetUserPassword.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(resetUserPassword.fulfilled, (state, action) => {
        state.isLoading = false;
        // Optional: store message in state if needed
        state.successMessage = action.payload.message;
      })
      .addCase(resetUserPassword.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getUserEffectivePermissions.pending, (state) => {
        state.effectivePermissionsLoading = true;
        state.error = null;
      })
      .addCase(getUserEffectivePermissions.fulfilled, (state, action) => {
        state.effectivePermissionsLoading = false;
        state.userEffectivePermissions = action.payload;
      })
      .addCase(getUserEffectivePermissions.rejected, (state, action) => {
        state.effectivePermissionsLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, setCurrentUser, clearSearchResults } = userSlice.actions;
export default userSlice.reducer;
